<script setup>
  import { ref, inject, onMounted, onUnmounted, computed, nextTick, onBeforeUnmount } from 'vue'
  import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataTable from '@/components/DataTable.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import { useStorageStore } from '@/stores/storageStore'
  import { useDialogStore } from '@/stores/dialogStore'
  import QueueProgressPanel from '@/components/QueueProgressPanel.vue'

  const router = useRouter()
  const route = useRoute()

  const storageStore = useStorageStore()
  const dialogStore = useDialogStore()
  const svgText = ref(null)
  const svgTextWidth = ref(0)
  const companyName = computed(() => {
    nextTick(() => {
      if (svgText.value) {
        svgTextWidth.value = svgText.value.getComputedTextLength()
      }
    })
    return storageStore.companyName
  })

  // パラメータから会社UUIDを取得
  const companyUuid = route.params.uuid

  // data
  const searchText = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')
  const uploadInput = ref(null)
  const showCreateFolderDialog = ref(false)
  const showRenameDialog = ref(false)
  const folderName = ref('')
  const newFileName = ref('')
  const renameTarget = ref(null)

  // パンくずリスト
  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '会社一覧', url: '../company' }, { name: 'ストレージ' }]

  // ドラッグ&ドロップの状態管理
  const isDragOver = ref(false)
  const dragCounter = ref(0) // ドラッグイベントのカウンター

  // ドラッグエンターハンドラー
  const dragenterHandler = event => {
    event.preventDefault()
    event.stopPropagation()

    // ファイルがドラッグされているかチェック
    if (event.dataTransfer.types && event.dataTransfer.types.indexOf('Files') !== -1) {
      dragCounter.value++
      isDragOver.value = true
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  // ドラッグオーバーハンドラー（ドロップを許可するために必要）
  const dragoverHandler = event => {
    event.preventDefault()
    event.stopPropagation()

    if (event.dataTransfer.types && event.dataTransfer.types.indexOf('Files') !== -1) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  // ドラッグリーブハンドラー
  const dragleaveHandler = event => {
    event.preventDefault()
    event.stopPropagation()

    dragCounter.value--
    if (dragCounter.value === 0) {
      isDragOver.value = false
    }
  }

  // ドロップハンドラー
  const dropHandler = async event => {
    event.preventDefault()
    event.stopPropagation()

    // カウンターとフラグをリセット
    dragCounter.value = 0
    isDragOver.value = false

    // dataTransfer.itemsを使用してファイルとフォルダを処理
    if (event.dataTransfer.items) {
      // 最初にエントリーを取得して保存
      const entries = []
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i]
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : item.getAsEntry()
          if (entry) {
            entries.push(entry)
          }
        }
      }

      // エントリーを取得した後で確認ダイアログを表示
      if (!(await dialogStore.showConfirmDialog('確認', 'ファイルをアップロードします。同名ファイルは上書きされますのでご注意ください。'))) {
        return
      }

      // 保存したエントリーを処理
      await handleDroppedEntries(entries)
    } else if (event.dataTransfer.files) {
      // filesの場合も先に配列に変換
      const fileArray = Array.from(event.dataTransfer.files)

      if (!(await dialogStore.showConfirmDialog('確認', 'ファイルをアップロードします。同名ファイルは上書きされますのでご注意ください。'))) {
        return
      }

      // フォールバック：通常のファイル処理
      await handleDroppedFiles(fileArray)
    }
  }

  // 新しい関数：エントリーの配列を処理
  const handleDroppedEntries = async entries => {
    try {
      // 現在のパスを保存（処理中に変更される可能性があるため）
      const targetPath = storageStore.currentPath
      const fileStructure = new Map() // パス -> ファイル配列のマップ
      const rootFiles = []

      // 処理待ちのプロミスを収集
      const promises = []

      // 各エントリーを処理
      for (const entry of entries) {
        promises.push(processEntry(entry, '', fileStructure, rootFiles))
      }

      // 全ての処理が完了するまで待つ
      await Promise.all(promises)

      // 事前にフォルダの作成を行う
      for (const [path, files] of fileStructure.entries()) {
        // 一時的に現在のパスを保存し、フォルダ作成時は適切なパスに設定
        const originalPath = storageStore.currentPath

        // ドロップされたフォルダの親パスを設定
        const parentPath = targetPath || ''
        const folderName = path

        // 親パスに一時的に移動してフォルダを作成
        storageStore.currentPath = parentPath
        await storageStore.createFolder(folderName, false)

        // 元のパスに戻す
        storageStore.currentPath = originalPath
      }

      // reload storage data（元のパスを維持）
      await storageStore.getFiles(targetPath)

      // フォルダ内のファイルをアップロード
      for (const [path, files] of fileStructure.entries()) {
        if (files.length > 0) {
          for (const file of files) {
            // ドロップされたフォルダパスと現在のパスを結合してファイルパスを設定
            const fullPath = targetPath ? (path ? `${targetPath}/${path}` : targetPath) : path
            file._path = fullPath || '/'
          }
          await storageStore.uploadMultipleFiles(files)
        }
      }

      // ルートレベルのファイルをアップロード
      if (rootFiles.length > 0) {
        await uploadRootFiles(rootFiles, targetPath)
      }
    } catch (err) {
      await dialogStore.showAlertDialog('エラー', `ファイルの処理に失敗しました: ${err.message}`)
    }
  }

  // エントリー（ファイル/ディレクトリ）を再帰的に処理
  const processEntry = async (entry, path, fileStructure, rootFiles) => {
    return new Promise(async (resolve, reject) => {
      if (entry.isFile) {
        // ファイルの場合
        entry.file(
          file => {
            // ドットファイルは除外
            if (!file.name.startsWith('.')) {
              if (path) {
                if (!fileStructure.has(path)) {
                  fileStructure.set(path, [])
                }
                fileStructure.get(path).push(file)
              } else {
                rootFiles.push(file)
              }
            }
            resolve()
          },
          error => {
            resolve() // エラーでも処理を続行
          }
        )
      } else if (entry.isDirectory) {
        // ドットディレクトリは除外
        if (entry.name.startsWith('.')) {
          resolve()
          return
        }

        // ディレクトリの場合
        const dirPath = path ? `${path}/${entry.name}` : entry.name

        // 空のフォルダでも登録する
        if (!fileStructure.has(dirPath)) {
          fileStructure.set(dirPath, [])
        }

        const reader = entry.createReader()
        let allEntries = []

        // readEntriesを全て読み取るための関数
        const readAllEntries = async () => {
          return new Promise(resolveRead => {
            const readBatch = () => {
              reader.readEntries(
                async entries => {
                  if (entries.length === 0) {
                    resolveRead(allEntries)
                    return
                  }
                  allEntries = allEntries.concat(entries)
                  readBatch() // 次のバッチを読み取る
                },
                error => {
                  resolveRead(allEntries) // エラーでも今までの結果を返す
                }
              )
            }
            readBatch()
          })
        }

        // 全エントリーを読み取る
        const entries = await readAllEntries()

        // 各エントリーを処理
        const childPromises = []
        for (const childEntry of entries) {
          childPromises.push(processEntry(childEntry, dirPath, fileStructure, rootFiles))
        }

        // 全ての子要素の処理を待つ
        await Promise.all(childPromises)
        resolve()
      } else {
        resolve()
      }
    })
  }

  // ルートファイルをアップロード
  const uploadRootFiles = async (files, targetPath) => {
    // 同名ファイルをチェック
    const duplicateFiles = checkDuplicateFiles(files)
    if (duplicateFiles.length > 0) {
      if (!(await dialogStore.showConfirmDialog('確認', '同名ファイルは上書きされます。アップロードしますか？'))) {
        return
      }
    }

    // 指定されたパスをファイルに設定
    for (const file of files) {
      const filePath = targetPath || '/'
      file._path = filePath
    }

    // ファイルをアップロードキューに追加
    await storageStore.uploadMultipleFiles(files)
  }

  // ドロップされたファイルの処理（フォールバック用）
  const handleDroppedFiles = async files => {
    try {
      // ドットファイルを除外
      const fileArray = Array.from(files).filter(file => !file.name.startsWith('.'))
      if (fileArray.length === 0) return

      // 同名ファイルをチェック
      const duplicateFiles = checkDuplicateFiles(fileArray)

      if (duplicateFiles.length > 0) {
        // 同名ファイルが存在する場合、確認ダイアログを表示
        if (!(await dialogStore.showConfirmDialog('確認', '同名ファイルは上書きされます。アップロードしますか？'))) {
          return
        }
      }

      // 現在のパスをファイルに設定
      for (const file of fileArray) {
        const targetPath = storageStore.currentPath || '/'
        file._path = targetPath
      }

      // ファイルをアップロードキューに追加
      await storageStore.uploadMultipleFiles(fileArray)
    } catch (err) {
      await dialogStore.showAlertDialog('エラー', `ファイルのアップロードに失敗しました: ${err.message}`)
    }
  }

  // mounted
  onMounted(async () => {
    // 表示時にファイル一覧をクリアしてちらつきを防ぐ
    storageStore.clearFiles()

    // ストレージデータを取得
    storageStore.setCompanyUuid(companyUuid)
    await storageStore.getFiles()
    storageStore.initTableList()

    // 画面全体へのD&Dイベントの登録
    document.addEventListener('dragenter', dragenterHandler, false)
    document.addEventListener('dragover', dragoverHandler, false)
    document.addEventListener('dragleave', dragleaveHandler, false)
    document.addEventListener('drop', dropHandler, false)
  })

  onUnmounted(() => {
    // 画面全体へのD&Dイベントの解除
    document.removeEventListener('dragenter', dragenterHandler)
    document.removeEventListener('dragover', dragoverHandler)
    document.removeEventListener('dragleave', dragleaveHandler)
    document.removeEventListener('drop', dropHandler)

    // 状態をリセット
    dragCounter.value = 0
    isDragOver.value = false

    // ストレージ画面から離れる際にキューをクリア
    storageStore.clearQueue()
  })

  // methods
  // フォルダ作成ダイアログを開く
  const openCreateFolderDialog = () => {
    folderName.value = ''
    showCreateFolderDialog.value = true
  }

  // フォルダ作成
  const createFolder = async () => {
    if (!folderName.value.trim()) {
      await dialogStore.showMessageDialog('エラー', 'フォルダ名を入力してください')
      return
    }

    // フォルダ名のバリデーション
    const validation = validateFileName(folderName.value)
    if (!validation.valid) {
      await dialogStore.showAlertDialog('エラー', validation.message)
      return
    }

    const message = await storageStore.createFolder(folderName.value)
    if (message) {
      showCreateFolderDialog.value = false
      await dialogStore.showMessageDialog('確認', message)
    }
  }

  // ファイルアップロード
  const uploadFile = async () => {
    uploadInput.value.click()
  }

  // ファイル選択時の処理（キュー対応 + 同名ファイルチェック）
  const onFileSelected = async event => {
    const files = Array.from(event.target.files).filter(file => !file.name.startsWith('.'))
    if (files.length === 0) {
      uploadInput.value.value = ''
      return
    }

    // 同名ファイルをチェック
    const duplicateFiles = checkDuplicateFiles(files)

    if (duplicateFiles.length > 0) {
      // 同名ファイルが存在する場合、確認ダイアログを表示
      if (!(await dialogStore.showConfirmDialog('確認', '同名ファイルは上書きされます。アップロードしますか？'))) {
        // キャンセルされた場合、全てのファイルのアップロードを中止
        uploadInput.value.value = ''
        return
      }
    }

    for (const file of files) {
      file._path = storageStore.currentPath ? storageStore.currentPath : '/'
    }

    // 全てのファイルをキューに追加
    const addedIds = await storageStore.uploadMultipleFiles(files)

    // inputをクリア
    uploadInput.value.value = ''
  }

  // 同名ファイルをチェックする関数
  const checkDuplicateFiles = files => {
    const duplicateFiles = []
    const existingFiles = storageStore.files || []
    const existingFileNames = existingFiles.map(f => f.name)

    Array.from(files).forEach(file => {
      // 大文字小文字を区別して厳密比較
      if (existingFileNames.includes(file.name)) {
        duplicateFiles.push(file.name)
      }
    })

    return duplicateFiles
  }

  // ファイルサイズをチェックする関数
  const checkFileSizes = files => {
    const maxSize = 10 * 1024 * 1024 // 10MB
    const oversizedFiles = []

    Array.from(files).forEach(file => {
      if (file.size > maxSize) {
        oversizedFiles.push({
          name: file.name,
          size: formatFileSize(file.size),
        })
      }
    })

    return oversizedFiles
  }

  // ファイルサイズをフォーマットする関数
  const formatFileSize = bytes => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // ファイル名変更ダイアログを開く
  // ファイル名から拡張子を除去する関数
  const getFileNameWithoutExtension = filename => {
    const lastDot = filename.lastIndexOf('.')
    if (lastDot === -1) return filename
    return filename.substring(0, lastDot)
  }

  // ファイル名変更ダイアログを開く
  const openRenameDialog = file => {
    renameTarget.value = file
    newFileName.value = getFileNameWithoutExtension(file.name)
    showRenameDialog.value = true
  }

  // ファイル名の有効性をチェックする関数
  const validateFileName = fileName => {
    // Windows/Linuxで使用できない文字
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/

    // 予約語（Windows）
    const reservedNames = ['CON', 'PRN', 'AUX', 'NUL', 'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9', 'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9']

    // 空文字チェック
    if (!fileName || !fileName.trim()) {
      return { valid: false, message: 'ファイル名を入力してください' }
    }

    // 長さチェック（255文字制限）
    if (fileName.length > 255) {
      return { valid: false, message: 'ファイル名は255文字以内で入力してください' }
    }

    // 先頭・末尾のスペースやピリオドチェック
    if (fileName !== fileName.trim()) {
      return { valid: false, message: 'ファイル名の先頭・末尾にスペースを使用することはできません' }
    }

    if (fileName.endsWith('.')) {
      return { valid: false, message: 'ファイル名の末尾にピリオドを使用することはできません' }
    }

    // 無効文字チェック
    if (invalidChars.test(fileName)) {
      return { valid: false, message: 'ファイル名に次の文字は使用できません: < > : " / \\ | ? *' }
    }

    // 予約語チェック（大文字小文字を無視）
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName
    if (reservedNames.includes(nameWithoutExt.toUpperCase())) {
      return { valid: false, message: `「${nameWithoutExt}」はシステム予約語のため使用できません` }
    }

    // ピリオドのみのファイル名チェック
    if (/^\.+$/.test(fileName)) {
      return { valid: false, message: 'ピリオドのみのファイル名は使用できません' }
    }

    return { valid: true, message: '' }
  }

  // ファイル名変更（キュー対応）
  const renameFile = async () => {
    if (!newFileName.value.trim()) {
      await dialogStore.showMessageDialog('エラー', 'ファイル名を入力してください')
      return
    }

    // ファイル名のバリデーション
    const validation = validateFileName(newFileName.value)
    if (!validation.valid) {
      await dialogStore.showAlertDialog('エラー', validation.message)
      return
    }

    // 元の拡張子を取得
    const oldName = renameTarget.value.name
    const lastDot = oldName.lastIndexOf('.')
    let newName = newFileName.value
    if (lastDot !== -1) {
      const ext = oldName.substring(lastDot)
      newName = newFileName.value + ext
    }

    // 完全なファイル名（拡張子付き）のバリデーション
    const fullNameValidation = validateFileName(newName)
    if (!fullNameValidation.valid) {
      await dialogStore.showAlertDialog('エラー', fullNameValidation.message)
      return
    }

    // 同名ファイルチェック
    const existingFileNames = storageStore.files.map(f => f.name)
    if (existingFileNames.includes(newName) && newName !== oldName) {
      await dialogStore.showAlertDialog('エラー', '同名ファイルが存在します')
      return
    }

    const taskId = await storageStore.renameFileQueued(renameTarget.value.name, newName)
    showRenameDialog.value = false
  }

  // ファイル削除（キュー対応）
  const deleteFile = async file => {
    if (await dialogStore.showConfirmDialog('確認', `「${file.name}」を削除しますか？`)) {
      const taskId = await storageStore.deleteFileQueued(file.name)
      // await dialogStore.showMessageDialog('確認', `「${file.name}」の削除をキューに追加しました`)
    }
  }

  // ファイルダウンロード
  const downloadFile = async file => {
    const success = await storageStore.downloadFile(file.name)
    if (!success) {
      await dialogStore.showAlertDialog('エラー', 'ファイルのダウンロードに失敗しました')
    }
  }

  // パンくずリストをクリック時の処理
  const navigateToPath = path => {
    storageStore.navigateToPath(path)
  }

  // フォルダのインデックスでナビゲート
  const navigateToFolderIndex = index => {
    const folders = storageStore.currentPath.split('/')
    const targetPath = folders.slice(0, index + 1).join('/')
    storageStore.navigateToPath(targetPath)
  }

  // 行クリック時の処理
  const handleRowClick = item => {
    // 行クリック時のアクションはなし
  }

  // 行のアクション
  const handleRowAction = ({ table, item }) => {
    // 何もしない（アクションメニューは別で実装）
  }

  // ファイルアクション
  const handleFileAction = ({ table, item, event }) => {
    switch (table.key) {
      case 'download':
        downloadFile(item)
        break
      case 'rename':
        openRenameDialog(item)
        break
      case 'delete':
        deleteFile(item)
        break
    }
  }

  // 戻るボタン
  const goBack = () => {
    // 離脱時にファイル一覧をクリアして次回表示時のちらつきを防ぐ
    storageStore.clearFiles()
    // ストレージ画面から離れる際にキューをクリア
    storageStore.clearQueue()
    router.push({ name: 'company' })
  }

  // ルート離脱時のガード
  onBeforeRouteLeave((to, from, next) => {
    // ストレージ画面から離れる際にキューをクリア
    storageStore.clearQueue()
    next()
  })
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="goBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>
        <svg :width="svgTextWidth" height="31" xmlns="http://www.w3.org/2000/svg" :viewBox="`0 0 ${svgTextWidth} 31`">
          <text ref="svgText" x="0" y="24" font-size="27" fill="var(--active-color)">{{ companyName }} ストレージ</text>
        </svg>
      </h2>
      <div class="toolbar">
        <button type="button" class="new" @click="openCreateFolderDialog">フォルダ作成</button>
        <button type="button" class="new" @click="uploadFile">ファイルアップロード</button>
      </div>

      <div class="filters">
        <!-- 検索ボックス -->
        <!-- <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div> -->

        <div class="search-result">
          {{ length }}
        </div>
      </div>
      <!-- フォルダパス -->
    </div>
    <div class="folder-path">
      <span class="path-item" @click="navigateToPath('')">{{ companyName }}</span>
      <template v-if="storageStore.currentPath">
        <template v-for="(folder, index) in storageStore.currentPath.split('/')" :key="index">
          <span class="separator">/</span>
          <span class="path-item" @click="navigateToFolderIndex(index)">{{ folder }}</span>
        </template>
      </template>
    </div>

    <div class="main-body">
      <DataTable :table-list="storageStore.tableList" :data-list="storageStore.sortedFiles" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="handleRowClick" @length="length = $event" @row-action="handleFileAction"></DataTable>
    </div>

    <!-- ファイルアップロード用の隠しinput -->
    <input ref="uploadInput" type="file" multiple style="display: none" @change="onFileSelected" />
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <!-- フォルダ作成ダイアログ -->
  <EditDialog v-model:visible="showCreateFolderDialog" :show-save-button="true" :show-delete-button="false" :show-copy-button="false" :save-button-text="'作成'" @save="createFolder">
    <template v-slot:title>フォルダ作成</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>フォルダ名</dt>
          <dd>
            <input type="text" v-model="folderName" placeholder="フォルダ名を入力してください" maxlength="255" />
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <!-- ファイル名変更ダイアログ -->
  <EditDialog v-model:visible="showRenameDialog" :show-save-button="true" :show-delete-button="false" :show-copy-button="false" :save-button-text="'変更'" @save="renameFile">
    <template v-slot:title>ファイル名変更</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>ファイル名</dt>
          <dd>
            <input type="text" v-model="newFileName" placeholder="ファイル名を入力してください" maxlength="255" />
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <!-- キュー進捗パネル -->
  <QueueProgressPanel />

  <!-- ドラッグオーバーレイ -->
  <transition name="fade">
    <div class="drag-overlay" v-if="isDragOver" @drop="dropHandler" @dragover="dragoverHandler" @dragleave="dragleaveHandler">
      <div class="drag-content">
        <span class="material-symbols-outlined drag-icon">cloud_upload</span>
        <p class="drag-text">ファイルをここにドロップしてください</p>
        <p class="drag-subtext">複数ファイルの同時アップロードが可能です</p>
      </div>
    </div>
  </transition>
</template>

<style lang="less">
  // グローバルスタイル（このページのみ）
  html,
  body {
    margin: 0;
    padding: 0;
    height: 100%;
    overflow-x: auto;
  }

  #app {
    height: 100%;
  }
</style>

<style lang="less" scoped>
  // ドラッグオーバーレイのスタイル
  .drag-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
    pointer-events: all;
  }

  .drag-content {
    text-align: center;
    color: white;
    pointer-events: none;
  }

  .drag-icon {
    font-size: 80px;
    color: #4caf50;
    margin-bottom: 20px;
    display: block;
  }

  .drag-text {
    font-size: 24px;
    font-weight: 500;
    margin: 0 0 10px 0;
  }

  .drag-subtext {
    font-size: 16px;
    opacity: 0.8;
    margin: 0;
  }

  // フェードトランジション
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .app-main {
    grid-template-rows: auto auto 1fr;
    gap: 0;
    .main-header {
      padding-bottom: 30px;
    }
  }

  .folder-path {
    display: flex;
    align-items: center;
    padding: 0 20px;
    font-size: 12px;
    margin-bottom: 10px;
  }

  .toolbar {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .path-item {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  .company-subtitle {
    font-size: 0.9rem;
    color: #666;
    margin-left: 12px;
    vertical-align: middle;
  }

  .separator {
    margin: 0 2px;
    color: #666;
    user-select: none;
  }

  // レスポンシブ対応
  @media (max-width: 768px) {
    .folder-path {
      flex-wrap: wrap;
    }

    .path-item {
      padding: 3px 6px;
    }

    .file-table {
      font-size: 0.9em;
    }

    .file-table th,
    .file-table td {
      padding: 8px;
    }

    .drag-icon {
      font-size: 60px;
    }

    .drag-text {
      font-size: 20px;
    }

    .drag-subtext {
      font-size: 14px;
    }
  }
</style>
